/*
 * NARBASE TECHNOLOGIES CONFIDENTIAL
 * ______________________________
 * [2017] - [2026] Narbase Technologies
 * All Rights Reserved.
 * Created by Omnia
 * On: 13/Oct/2026.
 */

package com.narbase.letsgo.web.views.ridepromocode

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.dimen
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.drawable.Color
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.datetime.toDate
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.scrollable.scrollable
import com.narbase.letsgo.web.utils.table.headerCell
import com.narbase.letsgo.web.utils.table.listTable
import com.narbase.letsgo.web.utils.table.tableCell
import com.narbase.letsgo.web.utils.table.tableRow
import com.narbase.letsgo.web.utils.verticalSeparator
import com.narbase.letsgo.web.utils.views.*

class RidePromoCodesManagementComponent : Component() {

    private var paginationControls: PaginationControls? = null
    private val viewModel = RidePromoCodesManagementViewModel()
    private var listTableBody: View? = null

    private val upsertDialog = UpsertRidePromoCodeDialog(
        activeAutoAssignProvider = { viewModel.activeAutoAssign },
        onUpdated = {
            viewModel.getList()
            viewModel.getActiveAutoAssign()
        }
    )

    override fun onViewMounted(lifecycleOwner: LifecycleOwner) {
        super.onViewMounted(lifecycleOwner)
        viewModel.getList()
        viewModel.getActiveAutoAssign()
    }

    private fun onListLoaded() {
        paginationControls?.update(viewModel.pageNo, viewModel.pageSize, viewModel.total)
        listTableBody?.clearAllChildren()
        listTableBody?.apply {
            viewModel.promoCodes.forEachIndexed { index, extended ->
                val item = extended.ridePromoCode
                tableRow {
                    style {
                        alignItems = Alignment.Center
                        paddingEnd = 8.px
                    }
                    tableCell(item.code, 2, 14.px)
                    tableCell(
                        if (item.isPercentage) "${item.percentage}%" else "${item.amount ?: ""} ${extended.currency?.name ?: ""}",
                        1, 14.px
                    )
                    tableCell(item.expiration.toDate().toDateString(), 1, 14.px)
                    tableCell("${item.actualUsersCount} / ${item.maxUsersCount}", 1, 14.px)
                    tableCell("${item.spending} / ${item.budget}", 2, 14.px)
                    tableCell(
                        listOfNotNull(
                            "Auto-assign".takeIf { item.autoAssignOnRegistration },
                            "App only".takeIf { item.isAppOnly }
                        ).joinToString(", ").ifBlank { "-" },
                        2, 14.px
                    )
                    onClick = {
                        upsertDialog.edit(item = extended)
                    }
                }
                if (index != viewModel.promoCodes.lastIndex) verticalSeparator()
            }
        }
    }

    override fun View?.getView() = view {
        id = "ridePromoCodesManagementRootView"
        style { matchParentDimensions }
        scrollable {
            style { matchParentDimensions }
            verticalLayout {
                style {
                    width = matchParent
                    height = wrapContent
                    padding = 32.px
                }
                horizontalLayout {
                    style { width = matchParent }
                    textView {
                        text = "Ride Promo Codes".localized()
                        style {
                            width = wrapContent
                            fontSize = 20.px
                            fontWeight = "bold"
                        }
                    }
                    horizontalFiller()
                    addButton()
                }
                horizontalLayout {
                    style {
                        width = matchParent
                        marginTop = 16.px
                    }
                    horizontalFiller()
                    searchTextInput("Search".localized()) { viewModel.searchFor(it) }
                }
                verticalLayout {
                    matchParentDimensions()
                    withLoadingAndError(
                        viewModel.getUiState,
                        onRetryClicked = { viewModel.getList() },
                        onLoaded = { onListLoaded() }
                    )
                    listTable {
                        headerCell("Code".localized(), 2)
                        headerCell("Value".localized(), 1)
                        headerCell("Expiration".localized(), 1)
                        headerCell("Users".localized(), 1)
                        headerCell("Spending / Budget".localized(), 2)
                        headerCell("Flags".localized(), 2)
                    }.also { listTableBody = it }
                    paginationControls = setupPaginationControls(viewModel::getNextPage, viewModel::getPreviousPage)
                }
            }
        }
    }

    private fun LinearLayout.addButton() {
        textView {
            style {
                color = Color.white
                padding = "2px 12px".dimen()
                backgroundColor = AppColors.mainColor
                borderRadius = 12.px
                pointerCursor()
                hover { backgroundColor = AppColors.mainDarkColor }
            }
            onClick = { upsertDialog.add() }
            id = "AddRidePromoCodeButton"
            text = "+ Add new promo code".localized()
        }
    }
}
